import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton, InputLabel } from '@mui/material';
import { useFormik } from 'formik';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import equipmentApi from '~/apis/equipment';
import { CloseIcon } from '~/assets/icons';
import BaseRow from '~/components/common/base-row/BaseRow';
import BaseSelect from '~/components/common/base-select/BaseSelect';
import { DIALOG_MODE_CREATE, DIALOG_MODE_EDIT } from '~/utils/constants/common';
import { handleApiError, notify } from '~/utils/helper';
import { IEquipment } from '~/utils/interface/equipment';
import equipmentValidation from '~/validation/equipment';

type EditEquipmentDialogProps = {
  open: boolean;
  locationId?: string;
  setOpen?: (open: boolean, needRefreshData: boolean) => void;
  data?: IEquipment;
  mode: string;
  onSetRefreshData?: (refresh: boolean) => void;
};

const initialValues: any = {
  device_code: '',
  device_name: '',
  agent_name: '',
};

const EditEquipmentDialog = (props: EditEquipmentDialogProps) => {
  const { t } = useTranslation('admin/equipment');

  const [deviceCodes, setDeviceCodes] = useState<any[]>([]);

  const form = useFormik({
    validateOnChange: false,
    initialValues,
    validationSchema: equipmentValidation(t).addDevice,
    onSubmit: async (values) => {
      if (props.mode === DIALOG_MODE_CREATE) {
        try {
          const deviceCode = deviceCodes.find((deviceCode) => deviceCode.id === values.device_code)?.name;
          const updateValues = {
            device_id: values.device_code,
            device_code: deviceCode,
            device_name: values.device_name,
            location_id: Number(props.locationId),
          };
          await equipmentApi.updateEquipment(updateValues);
          props.onSetRefreshData?.(true);
          notify('success', t('editEquipmentDialog.addSuccess'));
          handleSetOpen(false, true);
        } catch (e: any) {
          handleApiError(e, true);
        }
      }
    },
  });

  const handleSetOpen = (open: boolean, needRefreshData: boolean) => {
    form.resetForm();
    if (props.setOpen) {
      props.setOpen(open, needRefreshData);
    }
  };

  const getListEquipment = async () => {
    try {
      const res = await equipmentApi.getAll();
      const deviceCodes = res.data.map((device: any) => {
        return {
          id: device.device_id,
          name: device.device_code,
        };
      });
      setDeviceCodes(deviceCodes);
    } catch (error) {
      handleApiError(error);
      return { total: 0, devices: [] };
    }
  };

  useEffect(() => {
    getListEquipment();
  }, []);

  return (
    <>
      <Dialog open={props.open} onClose={() => handleSetOpen(false, false)} fullWidth maxWidth='xs'>
        <DialogTitle className='bg-[#005F73]'>
          {props.mode === DIALOG_MODE_EDIT ? t('editEquipmentDialog.editTitle') : t('editEquipmentDialog.title')}
          <IconButton onClick={() => handleSetOpen(false, false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component='form'>
            <BaseRow className='gap-[20px] mb-[20px] !items-center'>
              <Box className='w-1/2 font-bold flex items-center'>
                <InputLabel required={true}>{t('editEquipmentDialog.deviceCode')}</InputLabel>
                {' : '}
              </Box>
              <Box className='w-1/2'>
                <BaseSelect
                  size='medium'
                  options={deviceCodes}
                  value={form.values.device_code}
                  placeholder={t('select')}
                  name='device_code'
                  isSearchEnable
                  onChange={async (e: any) => {
                    const selectedId = e.target.value;
                    if (selectedId) {
                      try {
                        const deviceCode = deviceCodes.find((deviceCode) => deviceCode.id === selectedId)?.name;
                        form.setFieldValue('device_code', selectedId);
                        const res = await equipmentApi.searchDevice({ device_code: deviceCode });
                        const device = res.data;
                        form.setFieldValue('device_name', device[0]?.device_name);
                        form.setFieldValue('agent_name', device[0]?.agent_name);
                      } catch (error) {
                        handleApiError(error);
                      }
                    }
                  }}
                  error={Boolean(form.errors?.device_code)}
                  displayInvalidValue
                  helperText={form.errors?.device_code as string}
                />
              </Box>
            </BaseRow>

            <BaseRow className='gap-[20px] mb-[20px]'>
              <InputLabel className='w-1/2'>{t('editEquipmentDialog.deviceName')}:</InputLabel>
              <Box className='w-1/2 text-end font-bold'>{form.values.device_name}</Box>
            </BaseRow>
            <BaseRow className='gap-[20px] mb-[20px]'>
              <InputLabel className='w-1/2 '>{t('agency')}</InputLabel>
              <Box className='w-1/2 text-end font-bold'>{form.values.agent_name}</Box>
            </BaseRow>
            <Button
              variant='contained'
              color='secondary'
              className='min-w-[125px] flex ml-auto'
              onClick={() => form.handleSubmit()}
            >
              {props.mode === DIALOG_MODE_EDIT ? t('editEquipmentDialog.save') : t('editEquipmentDialog.add')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default memo(EditEquipmentDialog);
